export type SelectedMeta = {
  benefitKey: string;
  identifier: string;
  comment: string;
};

const TEMPLATES: Record<
  string,
  {
    groupHeader: string;
    itemTemplate: string;
    itemSeparator: string;
    groupSeparator: string;
  }
> = {
  markdown: {
    groupHeader: "# {{benefitKey}}",
    itemTemplate: "**{{identifier}}**\n{{comment}}\n",
    itemSeparator: "\n",
    groupSeparator: "\n\n",
  },
  plaintext: {
    groupHeader: "{{benefitKey}}:",
    itemTemplate: "{{identifier}}\n{{comment}}\n",
    itemSeparator: "\n",
    groupSeparator: "\n\n",
  },
  word: {
    groupHeader: "<h2>{{benefitKey}}</h2>",
    itemTemplate: "<p><strong>{{identifier}}</strong><br/>{{comment}}</p>\n",
    itemSeparator: "\n",
    groupSeparator: "\n",
  },
};

// A single pass over `<[^>]+>` can leave a tag behind: stripping the inner tag
// of `<<b>script>` re-forms `<script>` in the output. Repeat until the string
// stops changing — each pass strictly shortens it, so this terminates.
const stripTags = (value: string): string => {
  let stripped = value;
  let previous = "";
  while (stripped !== previous) {
    previous = stripped;
    stripped = stripped.replace(/<[^>]+>/g, "");
  }
  return stripped;
};

const groupByBenefitKey = (
  items: SelectedMeta[],
): Record<string, SelectedMeta[]> =>
  items.reduce((acc: Record<string, SelectedMeta[]>, item) => {
    (acc[item.benefitKey] = acc[item.benefitKey] || []).push(item);
    return acc;
  }, {});

export const formatWithTemplate = (
  items: SelectedMeta[],
  templateKey: string,
  benefitNameByKey: Record<string, string>,
): string => {
  const template = TEMPLATES[templateKey];
  if (!template) return "";

  const groups = groupByBenefitKey(items);
  let text = "";

  for (const [benefitKey, list] of Object.entries(groups)) {
    const benefitLabel = benefitNameByKey[benefitKey] || benefitKey;
    const headerPlain = stripTags(
      template.groupHeader.replace(
        /{{\s*benefitKey\s*}}/g,
        String(benefitLabel),
      ),
    );
    text += `${headerPlain}\n`;

    list.forEach((item) => {
      const itemPlain = stripTags(
        template.itemTemplate
          .replace(/{{\s*identifier\s*}}/g, String(item.identifier))
          .replace(/{{\s*comment\s*}}/g, String(item.comment ?? "")),
      );
      text += itemPlain + template.itemSeparator;
    });

    text += template.groupSeparator;
  }

  return text.replace(/[\s\n]+$/g, "");
};

export const formatAsHTML = (
  items: SelectedMeta[],
  templateKey: string,
  benefitNameByKey: Record<string, string>,
): string => {
  const template = TEMPLATES[templateKey];
  if (!template) return "";

  const groups = groupByBenefitKey(items);
  let html = "<div>";

  for (const [benefitKey, list] of Object.entries(groups)) {
    const benefitLabel = benefitNameByKey[benefitKey] || benefitKey;
    const header = template.groupHeader.replace(
      /{{\s*benefitKey\s*}}/g,
      String(benefitLabel),
    );
    html += header;

    list.forEach((item) => {
      let itemHtml = template.itemTemplate.replace(
        /{{\s*identifier\s*}}/g,
        String(item.identifier),
      );
      itemHtml = itemHtml.replace(
        /{{\s*comment\s*}}/g,
        String(item.comment ?? ""),
      );
      html += itemHtml;
    });

    html += template.groupSeparator.replace(/\n/g, "<br/>");
  }

  html += "</div>";
  return html;
};

export const copyToClipboard = async (
  textPlain: string,
  textHtml?: string,
): Promise<void> => {
  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      const clipData: Record<string, Blob> = {};
      if (textHtml)
        clipData["text/html"] = new Blob([textHtml], { type: "text/html" });
      clipData["text/plain"] = new Blob([textPlain], { type: "text/plain" });
      await navigator.clipboard.write([new ClipboardItem(clipData)]);
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(textPlain);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = textPlain;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  } catch {
    // ignore
  }
};
